import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { api, apiHolder, apis, EApprovalAttribute, jsonSafeParse } from 'api';
import { getIsEdmAdmin } from '@web-common/state/reducer/privilegeReducer';
import { findMenuBadgeConditionOk, getNewMenuBadgeList } from '@web-common/utils/utils';
const approvalFlowApi = apiHolder.api.requireLogicalApi(apis.approvalFlowApiImpl);
const siteApi = api.requireLogicalApi(apis.siteApiImpl);
const dataStoreApi = apiHolder.api.getDataStoreApi();
const waApi = api.requireLogicalApi(apis.insertWhatsAppApiImpl);
const InitialState = {
    unreadCount: {},
    menuBadgeList: [],
};
const MENU_BADGE_BASE_STORE_KEY = 'menu_badge';
const genMenuBadgeStoreKey = (path, contentType) => `${MENU_BADGE_BASE_STORE_KEY}_${path}_${contentType}`;
export const getToApprovalByMeCount = createAsyncThunk('readCount/getToApprovalByMeCount', async () => {
    return approvalFlowApi.doGetApprovalRecordListPage({ tab: EApprovalAttribute.toApprovalByMe, page: 1, pageSize: 1 });
});
export const registerMenuBadgeHandler = async (params) => {
    var _a;
    const { id: originId, path, rootPath, content = '', contentType, styleType = 'regular', clickRemove = false, priority, activeDays = 90, compPosition, label, condition, useAnimation, } = params;
    const id = `${path}-${contentType}-${originId}`;
    const localKey = genMenuBadgeStoreKey(path, contentType);
    const { data: localStr } = await dataStoreApi.get(localKey);
    const localData = localStr ? jsonSafeParse(localStr) : null;
    let result = {
        id,
        originId,
        path,
        label,
        rootPath,
        content,
        contentType,
        styleType,
        clickRemove,
        compPosition,
        condition,
        priority: priority !== null && priority !== void 0 ? priority : 1,
        registerTime: Date.now(),
        removeTime: undefined,
        activeDays: Math.max(0, Math.min(90, activeDays)),
        useAnimation,
    };
    if (localData && localData.id === id) {
        result.registerTime = localData.registerTime;
        result.activeDays = localData.activeDays;
        const isLocalDataActive = localData.contentType === 'num' || (!localData.removeTime && Date.now() - localData.registerTime < activeDays * 24 * 60 * 60 * 1000);
        if (!isLocalDataActive) {
            result.removeTime = (_a = localData.removeTime) !== null && _a !== void 0 ? _a : Date.now();
        }
    }
    await dataStoreApi.put(localKey, JSON.stringify(result));
    return result.removeTime ? null : result;
};
export const registerMenuBadge = createAsyncThunk('readCount/registerMenuBadge', async (params, { getState }) => {
    const idMap = {};
    const result = [];
    const rootState = getState();
    const badgeList = rootState.readCountReducer.menuBadgeList;
    const version = rootState.privilegeReducer.version;
    const isUltimateVersion = rootState.privilegeReducer.ultimateVersion;
    const isAdmin = getIsEdmAdmin(rootState.privilegeReducer);
    const productVersion = rootState.privilegeReducer.version;
    const isNewUser = rootState.popupReducer.showNewUserGuide;
    for (const param of params) {
        const id = `${param.path}-${param.contentType}-${param.id}`;
        const inStore = badgeList.some(v => v.id === id);
        const conditionOk = version ? findMenuBadgeConditionOk({ isUltimateVersion, isAdmin, productVersion, isNewUser }, param.condition) : true;
        if (!idMap[id] && !inStore && conditionOk) {
            idMap[id] = true;
            const temp = await registerMenuBadgeHandler(param);
            if (temp) {
                result.push(temp);
            }
        }
    }
    return result;
});
export const removeMenuBadge = createAsyncThunk('readCount/removeMenuBadge', async (params) => {
    const { contentType, path } = params;
    const localKey = genMenuBadgeStoreKey(path, contentType);
    const { data: localStr } = await dataStoreApi.get(localKey);
    const localData = localStr ? jsonSafeParse(localStr) : null;
    if (localData) {
        const res = Object.assign(Object.assign({}, localData), { removeTime: Date.now() });
        await dataStoreApi.put(localKey, JSON.stringify(res));
    }
    return params;
});
export const getSiteMainUnreadCount = createAsyncThunk('readCount/getSiteMainUnreadCount', async () => {
    try {
        const { unConfigSiteNum = 0 } = await siteApi.getUnconfigWebSiteCounts();
        return unConfigSiteNum;
    }
    catch (error) {
        console.error('getSiteMainUnreadCount error', error);
        return 0;
    }
});
export const getWaChatUnreadCount = createAsyncThunk('readCount/getWaChatUnreadCount', async () => {
    try {
        const { unreadCount = 0 } = await waApi.getWAReddot();
        return unreadCount;
    }
    catch (error) {
        console.error('getWaChatUnreadCount error', error);
        return 0;
    }
});
const readCountSlice = createSlice({
    name: 'readCountReducer',
    initialState: InitialState,
    reducers: {
        updateMenuBadgeCount: (state, action) => {
            const { path, content, id } = action.payload;
            state.menuBadgeList = getNewMenuBadgeList(state.menuBadgeList, { path, content, id });
        },
        updateMenuBadgeText: (state, action) => {
            const { path, content, styleType, id } = action.payload;
            state.menuBadgeList = getNewMenuBadgeList(state.menuBadgeList, { path, content, styleType, id });
        },
        updateMailboxUnreadCount: (state, action) => {
            state.unreadCount.mailbox = action.payload;
            if (process.env.BUILD_ISEDM) {
                state.menuBadgeList = getNewMenuBadgeList(state.menuBadgeList, { path: 'mailbox', content: action.payload });
            }
        },
        updateIMUnreadCount: (state, action) => {
            state.unreadCount.message = action.payload;
            if (process.env.BUILD_ISEDM) {
                state.menuBadgeList = getNewMenuBadgeList(state.menuBadgeList, { path: 'message', content: action.payload });
            }
        },
        updateWAUnread: (state, action) => {
            state.unreadCount.wa = action.payload;
            state.menuBadgeList = getNewMenuBadgeList(state.menuBadgeList, { path: 'waChatList', content: action.payload });
        },
    },
    extraReducers(builder) {
        builder.addCase(getToApprovalByMeCount.fulfilled, (state, action) => {
            const content = action.payload.total || 0;
            state.unreadCount.approvalList = content;
            state.menuBadgeList = getNewMenuBadgeList(state.menuBadgeList, { path: 'approvalRecord', content });
        });
        builder.addCase(getSiteMainUnreadCount.fulfilled, (state, action) => {
            state.unreadCount.websiteMain = action.payload;
            state.menuBadgeList = getNewMenuBadgeList(state.menuBadgeList, { path: 'brand', content: action.payload });
        });
        builder.addCase(getWaChatUnreadCount.fulfilled, (state, action) => {
            state.unreadCount.wa = action.payload;
            state.menuBadgeList = getNewMenuBadgeList(state.menuBadgeList, { path: 'waChatList', content: action.payload });
        });
        builder.addCase(registerMenuBadge.fulfilled, (state, action) => {
            const items = action.payload;
            if (items.length > 0) {
                state.menuBadgeList = [...state.menuBadgeList, ...items];
            }
        });
        builder.addCase(removeMenuBadge.fulfilled, (state, action) => {
            const { path, contentType } = action.payload;
            state.menuBadgeList = state.menuBadgeList.filter(v => v.path !== path || v.contentType !== contentType);
        });
    },
});
export const { actions } = readCountSlice;
export default readCountSlice.reducer;
